import { Flex, NativeSelect, Text, Field, Dialog, Portal, CloseButton, Input } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import currencies from "@/utils/currencies.json";
import { Button, ErrorMessage } from "@digital-services-gard-as/anchor-react";
import { BankAccountDto, InvoiceFormData } from "@/types";
import { z } from "zod";
import { bankDetailsSchema } from "./schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getInvoiceSenderQueryOptions,
    postBankAccountMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";

type BankDetailsProps = {
    invoiceId: string;
    isOpen: boolean;
    onClose: () => void;
    defaultBankValues?: Partial<BankAccountDto>;
    crmId?: string;
    methods: ReturnType<typeof useForm<InvoiceFormData>>;
    setBankAccounts: React.Dispatch<React.SetStateAction<BankAccountDto[]>>;
};

type BankDetailsForm = z.infer<ReturnType<typeof bankDetailsSchema>>;

export const BankDetailsModal = ({
    invoiceId,
    isOpen,
    onClose,
    defaultBankValues,
    crmId,
    methods,
    setBankAccounts
}: BankDetailsProps) => {
    const { t } = useTranslation(["invoices", "errorMessages"]);
    const [submitError, setSubmitError] = useState<string>("");
    const queryClient = useQueryClient();

    const formValues = useMemo(
        () => ({
            bankName: "",
            swiftBicCode: "",
            currency: defaultBankValues?.currency || "",
            accountNumber: defaultBankValues?.accountNumber || "",
            accountHolder: "",
            branchNumber: "",
            correspondingBank: ""
        }),
        [defaultBankValues]
    ); // Debug log to check default values

    const bankAccountForm = useForm<BankDetailsForm>({
        resolver: zodResolver(bankDetailsSchema()),
        mode: "onBlur",
        defaultValues: formValues
    });
    const {
        formState: { errors: formErrors, isValid },
        register,
        handleSubmit,
        reset
    } = bankAccountForm;

    useEffect(() => {
        reset(formValues);
    }, [formValues, reset]);

    const { mutate: addBankAccount, isPending: isAddingBankAccount } = useMutation(postBankAccountMutationOptions);

    const onSubmit: SubmitHandler<BankDetailsForm> = data => {
        const bankAccount = {
            ...data,
            // eslint-disable-next-line no-magic-numbers
            currency: data.currency.substring(0, 3),
            branchNumber: data.branchNumber ?? "",
            correspondingBank: data.correspondingBank ?? ""
        };
        addBankAccount(
            { id: invoiceId, bankAccount },
            {
                onSuccess: createdBankAccount => {
                    queryClient.invalidateQueries(getInvoiceSenderQueryOptions(crmId ?? "", invoiceId));
                    setBankAccounts(prev => [...prev, createdBankAccount]);
                    toaster.create({
                        id: "bankAccountCreated",
                        title: t("invoices:createInvoice.bankDetails.newAccountRegistered"),
                        type: "success",
                        closable: true
                    });
                    methods.setValue("bankAccount", createdBankAccount, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true
                    });
                    methods.trigger("bankAccount");
                    onClose();
                },
                onError: (error: unknown) => {
                    const title = getErrorTitle(error, t("invoices:createInvoice.bankDetails.newAccountFailed"));
                    toaster.create({ title, id: "bankAccountFailed", closable: true, type: "error" });
                }
            }
        );
    };

    const onErrorSubmit: SubmitErrorHandler<BankDetailsForm> = errors => {
        if (!isValid) {
            const amountOfErrors = Object.keys(errors).length;
            setSubmitError(
                amountOfErrors === 0 ? "" : t("errorMessages:invoiceForm.errorAmount", { amount: amountOfErrors })
            );
        }
    };

    const handleCloseModalClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            onClose();
        },
        [onClose]
    );

    const handleOnOpenChange = useCallback(
        (e: { open: boolean }) => {
            if (!e.open) {
                onClose();
            }
        },
        [onClose]
    );

    return (
        <Dialog.Root open={isOpen} data-testid="bankDetailsModal" onOpenChange={handleOnOpenChange}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content data-testid="bankDetailsModal">
                        <Dialog.Header>
                            <Dialog.Title>{t("invoices:createInvoice.bankDetails.newAccountTitle")}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.CloseTrigger asChild data-testid="bankDetailsModalCloseBtn">
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                        <Dialog.Body>
                            <Text>{t("invoices:createInvoice.bankDetails.newAccountDesc")}</Text>
                            <Flex flexDir="column" mt="2rem" gap="2.5rem">
                                <Field.Root invalid={Boolean(formErrors.bankName?.message)} maxW="20rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.bankName")}
                                    </Field.Label>
                                    <Input id="bankName" {...register("bankName")} />
                                    <ErrorMessage size="sm" data-testid="bankNameError">
                                        {formErrors.bankName?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.swiftBicCode?.message)} maxW="10rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.swiftBicCode")}
                                    </Field.Label>
                                    <Input id="swiftBicCode" {...register("swiftBicCode")} />
                                    <ErrorMessage size="sm" data-testid="swiftBicCodeError">
                                        {formErrors.swiftBicCode?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.currency?.message)}>
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.currency")}
                                    </Field.Label>
                                    <NativeSelect.Root>
                                        <NativeSelect.Field
                                            id="currencySelect"
                                            {...register("currency")}
                                            placeholder={t("invoices:createInvoice.bankDetails.selectCurrency")}
                                        >
                                            {Object.entries(currencies).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {key}: {value}
                                                </option>
                                            ))}
                                        </NativeSelect.Field>
                                        <NativeSelect.Indicator />
                                    </NativeSelect.Root>
                                    <ErrorMessage size="sm" data-testid="currencyError">
                                        {formErrors.currency?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.accountNumber?.message)} maxW="25rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.accountNumber")}
                                    </Field.Label>
                                    <Input id="accountNumber" {...register("accountNumber")} />
                                    <ErrorMessage size="sm" data-testid="accountNumberError">
                                        {formErrors.accountNumber?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.accountHolder?.message)} maxW="20rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.accountHolder")}
                                    </Field.Label>
                                    <Input id="accountHolder" {...register("accountHolder")} />
                                    <ErrorMessage size="sm" data-testid="accountHolderError">
                                        {formErrors.accountHolder?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.branchNumber?.message)} maxW="15rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.branchNumber")}
                                    </Field.Label>
                                    <Input id="branchNumber" {...register("branchNumber")} />
                                    <ErrorMessage size="sm" data-testid="branchNumberError">
                                        {formErrors.branchNumber?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Field.Root invalid={Boolean(formErrors.correspondingBank?.message)} maxW="20rem">
                                    <Field.Label fontWeight="anchor.bold">
                                        {t("invoices:createInvoice.bankDetails.correspondingBank")}
                                    </Field.Label>
                                    <Input id="correspondingBank" {...register("correspondingBank")} />
                                    <ErrorMessage size="sm" data-testid="correspondingBankError">
                                        {formErrors.correspondingBank?.message}
                                    </ErrorMessage>
                                </Field.Root>
                                <Flex gap={"1rem"}>
                                    <Button
                                        id="submitBtn"
                                        type="button"
                                        loading={isAddingBankAccount}
                                        disabled={isAddingBankAccount}
                                        onClick={handleSubmit(onSubmit, onErrorSubmit)}
                                    >
                                        {t("invoices:createInvoice.bankDetails.addBankAccountBtn")}
                                    </Button>
                                    <Button
                                        type="button"
                                        id="closeBankAccountModalBtn"
                                        variant="secondary"
                                        onClick={handleCloseModalClick}
                                    >
                                        {t("invoices:createInvoice.bankDetails.closeBankAccountModalBtn")}
                                    </Button>
                                </Flex>
                                <Text color="anchor.red.900">{submitError}</Text>
                            </Flex>
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

// Small helper to derive a readable error title
const getErrorTitle = (err: unknown, fallback: string): string => {
    const axiosErr = err as AxiosError | undefined;
    const data = axiosErr?.response?.data;
    if (typeof data === "string") {
        return data;
    }
    const msg = (data as { message?: unknown })?.message;
    if (typeof msg === "string") {
        return msg;
    }
    return axiosErr?.message ?? fallback;
};
